import * as T from './voiceAgentConstants';
import type {
  VoiceAgentSummary,
  TopItemsResponse,
  TimeSavedResponse,
  OrdersByDayResponse,
  VoiceOrdersResponse,
  ConversionFunnelResponse,
  CallActivityLogResponse,
  CallActivityLogOutcomesResponse,
  CallTranscriptResponse,
  CallVolumeByHourResponse,
  CallVolumeByDayResponse,
  OutcomeDistributionResponse,
  RevenueKpiResponse,
  TopRevenueItemsResponse,
  TransfersResponse,
  TransferReasonsResponse,
} from '../../../services/voiceAgentApi';

export interface VoiceAgentRangeParams {
  locationId: string;
  from: string;
  to: string;
}

// `from`/`to` are optional — omitted for the "Today" date filter (see the
// "Today"-omission comments on the corresponding getVoiceAgent* functions
// in services/voiceAgentApi.ts). Shared by every request whose endpoint
// accepts an optional range: Summary, Top Items, Time Saved, Call Volume by
// Hour, and Call Activity Log.
export interface VoiceAgentOptionalRangeParams {
  locationId: string;
  from?: string;
  to?: string;
}

// ── Summary ──────────────────────────────────────────────────────────────

export const fetchVoiceAgentSummaryRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_SUMMARY_REQUEST as typeof T.FETCH_VOICE_AGENT_SUMMARY_REQUEST,
  payload: params,
});

export const fetchVoiceAgentSummarySuccess = (data: VoiceAgentSummary) => ({
  type: T.FETCH_VOICE_AGENT_SUMMARY_SUCCESS as typeof T.FETCH_VOICE_AGENT_SUMMARY_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentSummaryFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_SUMMARY_FAILURE as typeof T.FETCH_VOICE_AGENT_SUMMARY_FAILURE,
  payload: { error },
});

// ── Top Items ────────────────────────────────────────────────────────────

export const fetchVoiceAgentTopItemsRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_TOP_ITEMS_REQUEST as typeof T.FETCH_VOICE_AGENT_TOP_ITEMS_REQUEST,
  payload: params,
});

export const fetchVoiceAgentTopItemsSuccess = (data: TopItemsResponse) => ({
  type: T.FETCH_VOICE_AGENT_TOP_ITEMS_SUCCESS as typeof T.FETCH_VOICE_AGENT_TOP_ITEMS_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentTopItemsFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_TOP_ITEMS_FAILURE as typeof T.FETCH_VOICE_AGENT_TOP_ITEMS_FAILURE,
  payload: { error },
});

// ── Time Saved ───────────────────────────────────────────────────────────

export const fetchVoiceAgentTimeSavedRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_TIME_SAVED_REQUEST as typeof T.FETCH_VOICE_AGENT_TIME_SAVED_REQUEST,
  payload: params,
});

export const fetchVoiceAgentTimeSavedSuccess = (data: TimeSavedResponse) => ({
  type: T.FETCH_VOICE_AGENT_TIME_SAVED_SUCCESS as typeof T.FETCH_VOICE_AGENT_TIME_SAVED_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentTimeSavedFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_TIME_SAVED_FAILURE as typeof T.FETCH_VOICE_AGENT_TIME_SAVED_FAILURE,
  payload: { error },
});

// ── Orders By Day ────────────────────────────────────────────────────────

export const fetchVoiceAgentOrdersByDayRequest = (params: VoiceAgentRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_REQUEST as typeof T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_REQUEST,
  payload: params,
});

export const fetchVoiceAgentOrdersByDaySuccess = (data: OrdersByDayResponse) => ({
  type: T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_SUCCESS as typeof T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentOrdersByDayFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_FAILURE as typeof T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_FAILURE,
  payload: { error },
});

// ── Recent Voice Orders (paginated) ─────────────────────────────────────

export interface VoiceAgentOrdersRequestParams extends VoiceAgentOptionalRangeParams {
  page: number;
  limit: number;
}

export const fetchVoiceAgentOrdersRequest = (params: VoiceAgentOrdersRequestParams) => ({
  type: T.FETCH_VOICE_AGENT_ORDERS_REQUEST as typeof T.FETCH_VOICE_AGENT_ORDERS_REQUEST,
  payload: params,
});

export const fetchVoiceAgentOrdersSuccess = (data: VoiceOrdersResponse) => ({
  type: T.FETCH_VOICE_AGENT_ORDERS_SUCCESS as typeof T.FETCH_VOICE_AGENT_ORDERS_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentOrdersFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_ORDERS_FAILURE as typeof T.FETCH_VOICE_AGENT_ORDERS_FAILURE,
  payload: { error },
});

// ── Conversion Funnel ────────────────────────────────────────────────────

export const fetchVoiceAgentConversionFunnelRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_REQUEST as typeof T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_REQUEST,
  payload: params,
});

export const fetchVoiceAgentConversionFunnelSuccess = (data: ConversionFunnelResponse) => ({
  type: T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_SUCCESS as typeof T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentConversionFunnelFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_FAILURE as typeof T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_FAILURE,
  payload: { error },
});

// ── Call Activity Log (paginated + outcome-filtered) ────────────────────

export interface VoiceAgentCallActivityLogRequestParams extends VoiceAgentOptionalRangeParams {
  outcome?: string;
  sortBy?: 'duration' | 'time' | 'revenue';
  sortOrder?: 'asc' | 'desc';
  page: number;
  limit: number;
}

export const fetchVoiceAgentCallActivityLogRequest = (params: VoiceAgentCallActivityLogRequestParams) => ({
  type: T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_REQUEST as typeof T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_REQUEST,
  payload: params,
});

export const fetchVoiceAgentCallActivityLogSuccess = (data: CallActivityLogResponse) => ({
  type: T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_SUCCESS as typeof T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentCallActivityLogFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_FAILURE as typeof T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_FAILURE,
  payload: { error },
});

// ── Call Activity Log — outcome filter options ──────────────────────────

export const fetchVoiceAgentCallActivityLogOutcomesRequest = (locationId: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_REQUEST as typeof T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_REQUEST,
  payload: { locationId },
});

export const fetchVoiceAgentCallActivityLogOutcomesSuccess = (data: CallActivityLogOutcomesResponse) => ({
  type: T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_SUCCESS as typeof T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentCallActivityLogOutcomesFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_FAILURE as typeof T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_FAILURE,
  payload: { error },
});

// value is 'all' or a backend-defined outcome string (see CallActivityLogOutcomesResponse).
export const setCallActivityLogOutcomeFilter = (value: string) => ({
  type: T.SET_CALL_ACTIVITY_LOG_OUTCOME_FILTER as typeof T.SET_CALL_ACTIVITY_LOG_OUTCOME_FILTER,
  payload: value,
});

// ── Call Transcript ──────────────────────────────────────────────────────

export const fetchVoiceAgentCallTranscriptRequest = (callSid: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_REQUEST as typeof T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_REQUEST,
  payload: { callSid },
});

export const fetchVoiceAgentCallTranscriptSuccess = (data: CallTranscriptResponse) => ({
  type: T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_SUCCESS as typeof T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentCallTranscriptFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_FAILURE as typeof T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_FAILURE,
  payload: { error },
});

export const clearVoiceAgentCallTranscript = () => ({
  type: T.CLEAR_VOICE_AGENT_CALL_TRANSCRIPT as typeof T.CLEAR_VOICE_AGENT_CALL_TRANSCRIPT,
});

// ── Call Volume by Hour ──────────────────────────────────────────────────

export const fetchVoiceAgentCallVolumeByHourRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_REQUEST as typeof T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_REQUEST,
  payload: params,
});

export const fetchVoiceAgentCallVolumeByHourSuccess = (data: CallVolumeByHourResponse) => ({
  type: T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_SUCCESS as typeof T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentCallVolumeByHourFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_FAILURE as typeof T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_FAILURE,
  payload: { error },
});

// ── Call Volume by Day ───────────────────────────────────────────────────

export const fetchVoiceAgentCallVolumeByDayRequest = (params: VoiceAgentRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_REQUEST as typeof T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_REQUEST,
  payload: params,
});

export const fetchVoiceAgentCallVolumeByDaySuccess = (data: CallVolumeByDayResponse) => ({
  type: T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_SUCCESS as typeof T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentCallVolumeByDayFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_FAILURE as typeof T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_FAILURE,
  payload: { error },
});

// ── Outcome Distribution ─────────────────────────────────────────────────

export const fetchVoiceAgentOutcomeDistributionRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_REQUEST as typeof T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_REQUEST,
  payload: params,
});

export const fetchVoiceAgentOutcomeDistributionSuccess = (data: OutcomeDistributionResponse) => ({
  type: T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_SUCCESS as typeof T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentOutcomeDistributionFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_FAILURE as typeof T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_FAILURE,
  payload: { error },
});

// ── Voice Revenue KPI ────────────────────────────────────────────────────

// `from`/`to` are optional — the "Today" date filter omits them entirely so
// the API isn't sent a from/to pair at all (see getVoiceAgentRevenueKpi).
export interface VoiceAgentRevenueKpiParams {
  locationId: string;
  from?: string;
  to?: string;
}

export const fetchVoiceAgentRevenueKpiRequest = (params: VoiceAgentRevenueKpiParams) => ({
  type: T.FETCH_VOICE_AGENT_REVENUE_KPI_REQUEST as typeof T.FETCH_VOICE_AGENT_REVENUE_KPI_REQUEST,
  payload: params,
});

export const fetchVoiceAgentRevenueKpiSuccess = (data: RevenueKpiResponse) => ({
  type: T.FETCH_VOICE_AGENT_REVENUE_KPI_SUCCESS as typeof T.FETCH_VOICE_AGENT_REVENUE_KPI_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentRevenueKpiFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_REVENUE_KPI_FAILURE as typeof T.FETCH_VOICE_AGENT_REVENUE_KPI_FAILURE,
  payload: { error },
});

// ── Top Revenue Items ────────────────────────────────────────────────────

// `from`/`to` are optional — same "Today" omission convention as revenue-kpi.
export interface VoiceAgentTopRevenueItemsParams {
  locationId: string;
  from?: string;
  to?: string;
}

export const fetchVoiceAgentTopRevenueItemsRequest = (params: VoiceAgentTopRevenueItemsParams) => ({
  type: T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_REQUEST as typeof T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_REQUEST,
  payload: params,
});

export const fetchVoiceAgentTopRevenueItemsSuccess = (data: TopRevenueItemsResponse) => ({
  type: T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_SUCCESS as typeof T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentTopRevenueItemsFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_FAILURE as typeof T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_FAILURE,
  payload: { error },
});

// ── Transfers ────────────────────────────────────────────────────────────

export const fetchVoiceAgentTransfersRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_TRANSFERS_REQUEST as typeof T.FETCH_VOICE_AGENT_TRANSFERS_REQUEST,
  payload: params,
});

export const fetchVoiceAgentTransfersSuccess = (data: TransfersResponse) => ({
  type: T.FETCH_VOICE_AGENT_TRANSFERS_SUCCESS as typeof T.FETCH_VOICE_AGENT_TRANSFERS_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentTransfersFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_TRANSFERS_FAILURE as typeof T.FETCH_VOICE_AGENT_TRANSFERS_FAILURE,
  payload: { error },
});

// ── Transfer Reasons ─────────────────────────────────────────────────────

export const fetchVoiceAgentTransferReasonsRequest = (params: VoiceAgentOptionalRangeParams) => ({
  type: T.FETCH_VOICE_AGENT_TRANSFER_REASONS_REQUEST as typeof T.FETCH_VOICE_AGENT_TRANSFER_REASONS_REQUEST,
  payload: params,
});

export const fetchVoiceAgentTransferReasonsSuccess = (data: TransferReasonsResponse) => ({
  type: T.FETCH_VOICE_AGENT_TRANSFER_REASONS_SUCCESS as typeof T.FETCH_VOICE_AGENT_TRANSFER_REASONS_SUCCESS,
  payload: data,
});

export const fetchVoiceAgentTransferReasonsFailure = (error: string) => ({
  type: T.FETCH_VOICE_AGENT_TRANSFER_REASONS_FAILURE as typeof T.FETCH_VOICE_AGENT_TRANSFER_REASONS_FAILURE,
  payload: { error },
});

export type VoiceAgentAction =
  | ReturnType<typeof fetchVoiceAgentSummaryRequest>
  | ReturnType<typeof fetchVoiceAgentSummarySuccess>
  | ReturnType<typeof fetchVoiceAgentSummaryFailure>
  | ReturnType<typeof fetchVoiceAgentTopItemsRequest>
  | ReturnType<typeof fetchVoiceAgentTopItemsSuccess>
  | ReturnType<typeof fetchVoiceAgentTopItemsFailure>
  | ReturnType<typeof fetchVoiceAgentTimeSavedRequest>
  | ReturnType<typeof fetchVoiceAgentTimeSavedSuccess>
  | ReturnType<typeof fetchVoiceAgentTimeSavedFailure>
  | ReturnType<typeof fetchVoiceAgentOrdersByDayRequest>
  | ReturnType<typeof fetchVoiceAgentOrdersByDaySuccess>
  | ReturnType<typeof fetchVoiceAgentOrdersByDayFailure>
  | ReturnType<typeof fetchVoiceAgentOrdersRequest>
  | ReturnType<typeof fetchVoiceAgentOrdersSuccess>
  | ReturnType<typeof fetchVoiceAgentOrdersFailure>
  | ReturnType<typeof fetchVoiceAgentConversionFunnelRequest>
  | ReturnType<typeof fetchVoiceAgentConversionFunnelSuccess>
  | ReturnType<typeof fetchVoiceAgentConversionFunnelFailure>
  | ReturnType<typeof fetchVoiceAgentCallActivityLogRequest>
  | ReturnType<typeof fetchVoiceAgentCallActivityLogSuccess>
  | ReturnType<typeof fetchVoiceAgentCallActivityLogFailure>
  | ReturnType<typeof fetchVoiceAgentCallActivityLogOutcomesRequest>
  | ReturnType<typeof fetchVoiceAgentCallActivityLogOutcomesSuccess>
  | ReturnType<typeof fetchVoiceAgentCallActivityLogOutcomesFailure>
  | ReturnType<typeof setCallActivityLogOutcomeFilter>
  | ReturnType<typeof fetchVoiceAgentCallTranscriptRequest>
  | ReturnType<typeof fetchVoiceAgentCallTranscriptSuccess>
  | ReturnType<typeof fetchVoiceAgentCallTranscriptFailure>
  | ReturnType<typeof clearVoiceAgentCallTranscript>
  | ReturnType<typeof fetchVoiceAgentCallVolumeByHourRequest>
  | ReturnType<typeof fetchVoiceAgentCallVolumeByHourSuccess>
  | ReturnType<typeof fetchVoiceAgentCallVolumeByHourFailure>
  | ReturnType<typeof fetchVoiceAgentCallVolumeByDayRequest>
  | ReturnType<typeof fetchVoiceAgentCallVolumeByDaySuccess>
  | ReturnType<typeof fetchVoiceAgentCallVolumeByDayFailure>
  | ReturnType<typeof fetchVoiceAgentOutcomeDistributionRequest>
  | ReturnType<typeof fetchVoiceAgentOutcomeDistributionSuccess>
  | ReturnType<typeof fetchVoiceAgentOutcomeDistributionFailure>
  | ReturnType<typeof fetchVoiceAgentRevenueKpiRequest>
  | ReturnType<typeof fetchVoiceAgentRevenueKpiSuccess>
  | ReturnType<typeof fetchVoiceAgentRevenueKpiFailure>
  | ReturnType<typeof fetchVoiceAgentTopRevenueItemsRequest>
  | ReturnType<typeof fetchVoiceAgentTopRevenueItemsSuccess>
  | ReturnType<typeof fetchVoiceAgentTopRevenueItemsFailure>
  | ReturnType<typeof fetchVoiceAgentTransfersRequest>
  | ReturnType<typeof fetchVoiceAgentTransfersSuccess>
  | ReturnType<typeof fetchVoiceAgentTransfersFailure>
  | ReturnType<typeof fetchVoiceAgentTransferReasonsRequest>
  | ReturnType<typeof fetchVoiceAgentTransferReasonsSuccess>
  | ReturnType<typeof fetchVoiceAgentTransferReasonsFailure>;
